// Playing area bookings, club admin side (BOOKINGS_PLAN.md §5). The network
// half: every verb endpoint behind one function, plus the per-club "are
// bookings on" state the Playing areas nav sub-item and the Setup page read.
// Pure maths and words live in utils/bookings.js.
import {computed, ref} from 'vue';
import {useState} from '#app';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints';
import {apiErrorMessage} from '~/utils/adminActionCopy';
import {parseRefusal} from '~/utils/bookings';
import {bookableBody} from '~/utils/bookingInPlace';

/** The API's reason, or a fallback; a CONFIRM_NEEDED / CHOICE_NEEDED prefix is stripped (the caller handles the step). */
export function bookingApiError(e, fallback = 'Something went wrong. Please try again') {
  return parseRefusal(apiErrorMessage(e, fallback)).message || fallback;
}

/** The refusal kind: 'confirm' (a preview wants a yes), 'choice' (keep or cancel) or 'error'. */
export function bookingRefusal(e) {
  return parseRefusal(apiErrorMessage(e, ''));
}

/**
 * Per-club bookings state: the super-admin rollout flag, whether bookings are
 * on and how many areas can be booked. Loaded by the admin layout once per
 * club; the Bookings and Setup pages refresh it after a change.
 */
export function useBookingsSetup() {
  const state = useState('bookingsSetup', () => ({clubId: null, featureEnabled: null, enabled: null, areasBookable: null, areasTotal: null, timeZone: null, rules: null}));
  const {httpRequest} = useHttpRequest();

  // X139 (R69 L-new-1): a page that already holds the club's booking settings (the rules set page fetches them with
  // its own server-rendered data) writes them here, so "Bookings are off" renders right from the first byte instead
  // of waiting for the layout's un-awaited load (the chip showed on the server for clubs with bookings ON, then
  // vanished: a hydration mismatch).
  const apply = (clubId, s) => {
    if (!clubId || !s) return;
    state.value = {
      clubId,
      featureEnabled: !!s?.bookingsFeatureEnabled,
      enabled: !!s?.bookingsEnabled,
      areasBookable: s?.areasBookable ?? 0,
      areasTotal: s?.areasTotal ?? 0,
      timeZone: s?.timeZone ?? null,
      // X133: how many sets of booking rules; the side menu's "Set up" chip stays until the first one exists.
      rules: s?.groups?.length ?? 0,
    };
  };

  const load = async (clubId) => {
    if (!clubId) return;
    try {
      const s = await httpRequest(endpoints.bookings.settings.get(clubId));
      apply(clubId, s);
      return s;
    } catch {
      // A member-only viewer, an old API or a blip: the nav shows no chip rather than a wrong one.
      state.value = {clubId, featureEnabled: null, enabled: null, areasBookable: null, areasTotal: null, timeZone: null, rules: null};
      return null;
    }
  };

  const bookingsFeature = computed(() => state.value.featureEnabled === true);
  const bookingsOn = computed(() => state.value.enabled === true);
  // X133 (Grant 2026-10-26): "Set up" beside Bookings until the first set of booking rules exists (a club with rules
  // that is merely switched off is set up; the page's banner says so). An old API without `groups` keeps the old rule.
  const needsSetup = computed(() => state.value.featureEnabled === true && (state.value.rules == null ? state.value.enabled === false : state.value.rules === 0));
  return {bookingsSetup: state, loadBookingsSetup: load, applyBookingsSettings: apply, bookingsFeature, bookingsOn, needsSetup};
}

export function useBookingsAdmin() {
  const {httpRequest} = useHttpRequest();
  const pending = ref(false);

  const call = async (endpoint, body) => {
    pending.value = true;
    try {
      return await httpRequest(endpoint, body ?? {});
    } finally {
      pending.value = false;
    }
  };

  // A 204 has no body: useHttpRequest's response.json() throws a SyntaxError on success.
  const callDelete = async (endpoint) => {
    pending.value = true;
    try {
      return await httpRequest(endpoint);
    } catch (e) {
      if (e instanceof SyntaxError) return true;
      throw e;
    } finally {
      pending.value = false;
    }
  };

  // ── Club settings (B1, B7b, B7d, B15, BE16, BE20) ──
  const getSettings = (clubId) => httpRequest(endpoints.bookings.settings.get(clubId));
  const updateSettings = (clubId, body) => call(endpoints.bookings.settings.update(clubId), body);
  const disablePreview = (clubId) => httpRequest(endpoints.bookings.settings.disablePreview(clubId));
  const timeZonePreview = (clubId, timeZone) => httpRequest(endpoints.bookings.settings.timeZonePreview(clubId, timeZone));
  const timeZones = () => httpRequest(endpoints.bookings.settings.timeZones);
  const verifyWebsite = (clubId) => call(endpoints.bookings.settings.verifyWebsite(clubId));

  // ── Area groups (B4, B4b, BE19, BE24) ──
  const listGroups = (clubId) => httpRequest(endpoints.bookings.areaGroups.list(clubId));
  const getGroup = (id) => httpRequest(endpoints.bookings.areaGroups.get(id));
  const createGroup = (body) => call(endpoints.bookings.areaGroups.create, body);
  const updateGroup = (id, body) => call(endpoints.bookings.areaGroups.update(id), body);
  const updateGroupPreview = (id, body) => httpRequest(endpoints.bookings.areaGroups.updatePreview(id), body);
  const deleteGroup = (id) => callDelete(endpoints.bookings.areaGroups.remove(id));
  const splitGroup = (id, body) => call(endpoints.bookings.areaGroups.split(id), body);
  const windowsPreview = (id, body) => httpRequest(endpoints.bookings.areaGroups.windowsPreview(id), body);
  const replaceWindows = (id, body) => call(endpoints.bookings.areaGroups.replaceWindows(id), body);
  const replaceWhoCanBook = (id, body) => call(endpoints.bookings.areaGroups.replaceWhoCanBook(id), body);

  // ── Playing areas (BE21, BE25) ──
  const areaChangePreview = (id, body) => httpRequest(endpoints.playingAreas.changePreview(id), body);
  // X96: the Setup page's own load and save (GET / PUT api/v2/playingAreas/{id}), for the in-place Can be booked switch.
  const getPlayingArea = (id) => httpRequest(endpoints.playingAreas.getItem(id));
  const updatePlayingArea = (id, body) => call(endpoints.playingAreas.updateItem(id), body);
  /** Can be booked on or off with every other field as the Setup page keeps it (bookableBody). */
  const setAreaBookable = async (id, canBeBooked, confirm = false) => updatePlayingArea(id, bookableBody(await getPlayingArea(id), canBeBooked, confirm));
  // X134: the set's page lists every playing area of the club (to move one under the set) and adds one straight into it.
  const listPlayingAreas = async (clubId) => (await httpRequest(endpoints.playingAreas.getItems, {page: 1, limit: 200, filters: {clubIds: [clubId]}}))?.items ?? [];
  const createPlayingArea = (body) => call(endpoints.playingAreas.addItem, body);

  // ── The calendar and bookings (B6, B13, B14, BE22) ──
  const availability = (clubId, query) => httpRequest(endpoints.bookings.bookings.availability(clubId, query));
  const listBookings = (filters) => httpRequest(endpoints.bookings.bookings.list, filters);
  const getBooking = (id) => httpRequest(endpoints.bookings.bookings.get(id));
  // X93: the All bookings tab (utils/allBookings builds the params from the route query).
  const listClubBookings = (params) => httpRequest(endpoints.bookings.clubBookings.list, params);
  const countClubBookings = (params) => httpRequest(endpoints.bookings.clubBookings.count, params);
  const downloadClubBookings = (params) => httpRequest(endpoints.bookings.clubBookings.download, params);
  const quote = (body) => httpRequest(endpoints.bookings.bookings.quote, body);
  const createBooking = (body) => call(endpoints.bookings.bookings.create, body);
  // X78 (R27 N5): `refund` asks the API to refund a booking that has already happened (nothing is refunded otherwise).
  const cancelBooking = (id, reason, refund = false) => call(endpoints.bookings.bookings.cancel(id), {reason: reason || null, ...(refund ? {refund: true} : {})});
  const releaseBooking = (id) => call(endpoints.bookings.bookings.release(id));
  const moveBooking = (id, body) => call(endpoints.bookings.bookings.move(id), body);
  // X36: the calendar's drag / resize preview (no pending flag: it runs while the block is still in hand).
  const movePreview = (id, body) => httpRequest(endpoints.bookings.bookings.movePreview(id), body);
  const markNoShow = (id) => call(endpoints.bookings.bookings.noShow(id));

  // ── Freezes (B9) ──
  const listClosures = (clubId, from) => httpRequest(endpoints.bookings.closures.list(clubId, from));
  const closurePreview = (body) => httpRequest(endpoints.bookings.closures.preview, body);
  const createClosure = (body) => call(endpoints.bookings.closures.create, body);
  const updateClosure = (id, body) => call(endpoints.bookings.closures.update(id), body);
  const deleteClosure = (id) => callDelete(endpoints.bookings.closures.remove(id));

  // ── X72: changes of a group's hours for single days ──
  const listDayOverrides = (clubId, from, to) => httpRequest(endpoints.bookings.dayOverrides.list(clubId, from, to));
  const dayOverridePreview = (body, id = null) => httpRequest(id ? endpoints.bookings.dayOverrides.updatePreview(id) : endpoints.bookings.dayOverrides.preview, body);
  const createDayOverride = (body) => call(endpoints.bookings.dayOverrides.create, body);
  const updateDayOverride = (id, body) => call(endpoints.bookings.dayOverrides.update(id), body);
  const deleteDayOverride = (id) => callDelete(endpoints.bookings.dayOverrides.remove(id));
  // X94 (R35 H3): "Change hours for this day" in one atomic call (the changes it replaces go in the same save).
  const replaceDayOverridesPreview = (body) => httpRequest(endpoints.bookings.dayOverrides.replacePreview, body);
  const replaceDayOverrides = (body) => call(endpoints.bookings.dayOverrides.replace, body);

  return {
    pending,
    getSettings, updateSettings, disablePreview, timeZonePreview, timeZones, verifyWebsite,
    listGroups, getGroup, createGroup, updateGroup, updateGroupPreview, deleteGroup, splitGroup, windowsPreview, replaceWindows, replaceWhoCanBook,
    areaChangePreview, getPlayingArea, updatePlayingArea, setAreaBookable, listPlayingAreas, createPlayingArea,
    availability, listBookings, getBooking, quote, createBooking, cancelBooking, releaseBooking, moveBooking, movePreview, markNoShow,
    listClosures, closurePreview, createClosure, updateClosure, deleteClosure,
    listDayOverrides, dayOverridePreview, createDayOverride, updateDayOverride, deleteDayOverride, replaceDayOverridesPreview, replaceDayOverrides,
    listClubBookings, countClubBookings, downloadClubBookings,
  };
}
